/**
 * Splide Init — shared initialization for all Splide sliders.
 *
 * Reads data attributes from the .splide element and mounts with
 * the correct config. Used by both the Slider section and Slider block.
 *
 * Usage:
 *   import { initSplideSlider } from './splide-init.js';
 *   initSplideSlider(document.querySelector('.splide'));
 *
 * Or auto-init all uninitialized sliders:
 *   import { initAllSliders } from './splide-init.js';
 *   initAllSliders();
 */

const INITIALIZED = 'data-splide-initialized';

// Gap token → px map (mirrors --space-gap-* from _variables.scss)
const GAP_MAP = { none: 0, '2xs': 8, xs: 16, sm: 20, md: 24, lg: 28, xl: 32, '2xl': 36 };

/** Resolve a gap value — token name, raw px string, or number */
const resolveGap = (v) => {
  if (!v || v === 'none') return '0px';
  if (v in GAP_MAP) return `${GAP_MAP[v]}px`;
  // Legacy: raw number or "16px"
  return v.includes('px') ? v : `${v}px`;
};

/**
 * Initialize a single Splide slider from its data attributes.
 * @param {HTMLElement} el - The .splide element
 * @returns {object|null} The Splide instance, or null if already initialized
 */
export function initSplideSlider(el) {
  if (!el || el.hasAttribute(INITIALIZED)) return null;
  // Skip product carousels — they have their own init in product-card.js
  if (el.classList.contains('product-carousel-splide')) return null;
  // Skip bare .splide elements with no data attributes (owned by other JS)
  if (!el.dataset.desktopSlides) return null;

  const d = el.dataset;
  const desktopSlides = parseInt(d.desktopSlides) || 1;
  const mobileSlides = parseInt(d.mobileSlides) || 1;
  const desktopScroll = parseInt(d.desktopScroll) || 1;
  const mobileScroll = parseInt(d.mobileScroll) || 1;
  const autoplay = d.autoplay === 'true';
  const autoplaySpeed = parseInt(d.autoplaySpeed) || 3000;
  const infinite = d.infinite === 'true';
  const showArrows = d.arrows === 'true';
  const showDots = d.dots === 'true';
  // Draggable always on, free scroll always off — hardcoded for simplicity
  const gap = resolveGap(d.gap || d.desktopGap);
  const mobileGap = resolveGap(d.mobileGap || d.gap || d.desktopGap);
  const desktopPeekLeft = d.desktopPeekLeft || d.desktopPeek || '0';
  const desktopPeekRight = d.desktopPeekRight || d.desktopPeek || '0';
  const mobilePeekLeft = d.mobilePeekLeft || d.mobilePeek || '0';
  const mobilePeekRight = d.mobilePeekRight || d.mobilePeek || '0';

  // Build padding — only apply non-zero values, append % if raw number
  const toPercent = (v) => {
    if (!v || v === '0' || v === '0%') return '0%';
    return v.includes('%') ? v : `${v}%`;
  };
  const padLeft = toPercent(desktopPeekLeft);
  const padRight = toPercent(desktopPeekRight);
  const mobPadLeft = toPercent(mobilePeekLeft);
  const mobPadRight = toPercent(mobilePeekRight);

  const hasPadding = padLeft !== '0%' || padRight !== '0%';
  const hasMobilePadding = mobPadLeft !== '0%' || mobPadRight !== '0%';

  const config = {
    type: infinite ? 'loop' : 'slide',
    perPage: desktopSlides,
    perMove: desktopScroll,
    autoplay,
    interval: autoplaySpeed,
    arrows: showArrows,
    pagination: showDots,
    drag: true,
    flickPower: 300,
    speed: 400,
    gap,
    trimSpace: !infinite,
    breakpoints: {
      768: {
        perPage: mobileSlides,
        perMove: mobileScroll,
        gap: mobileGap,
      },
    },
  };

  if (hasPadding) config.padding = { left: padLeft, right: padRight };
  if (hasMobilePadding) config.breakpoints[768].padding = { left: mobPadLeft, right: mobPadRight };

  const instance = new Splide(el, config).mount();

  el.setAttribute(INITIALIZED, 'true');
  return instance;
}

/**
 * Auto-init all .splide elements that haven't been initialized yet.
 * Skips elements with [data-lazy-init] (those are tab panels etc.).
 */
export function initAllSliders() {
  document.querySelectorAll(`.splide:not([${INITIALIZED}]):not([data-lazy-init])`).forEach((el) => {
    initSplideSlider(el);
  });
}

// Auto-init on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllSliders);
} else {
  initAllSliders();
}

// Re-init on Shopify editor section load
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', initAllSliders);
}

// Lazy-init for tabbed panels
document.addEventListener('splide:lazy-init', (e) => {
  const el = e.target;
  if (el?.matches?.('.splide')) {
    initSplideSlider(el);
  }
});
